import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { quoteRequestsApi } from '../services/api.ts';
import {
  QuoteRequest,
  QuoteRequestStatus,
  QUOTE_REQUEST_STATUS_LABELS,
  QUOTE_REQUEST_STATUS_COLORS,
  PRODUCT_INTEREST_LABELS,
} from '../types/index.ts';

const extractError = (e: any): string => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d.msg).join('; ');
  }
  return String(detail || 'An error occurred');
};

const QuoteRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<QuoteRequestStatus | ''>('');
  const [selected, setSelected] = useState<QuoteRequest | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const publicFormUrl = `${window.location.origin}/request-quote`;
  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicFormUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const { data, isLoading, refetch } = useQuery(
    ['quote-requests', search, statusFilter],
    () =>
      quoteRequestsApi.getRequests({
        search: search || undefined,
        status: (statusFilter as QuoteRequestStatus) || undefined,
        limit: 100,
      }),
    { keepPreviousData: true }
  );

  const updateMutation = useMutation(
    ({ id, status }: { id: number; status: QuoteRequestStatus }) =>
      quoteRequestsApi.updateRequest(id, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['quote-requests']);
        setSelected(null);
        setError('');
      },
      onError: (e: any) => setError(extractError(e)),
    }
  );

  const deleteMutation = useMutation(quoteRequestsApi.deleteRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries(['quote-requests']);
      setSelected(null);
    },
    onError: (e: any) => setError(extractError(e)),
  });

  const requests = data?.requests ?? [];

  const copyToClipboard = (text: string) =>
    navigator.clipboard.writeText(text).catch(() => {});

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Quote Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Inbound enquiries from the public form and customer portal
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          size="small"
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Public form link banner */}
      <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5}>
            <LinkIcon color="primary" />
            <Box flex={1}>
              <Typography variant="subtitle2" color="primary.dark">
                Customer quote request form
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Share this link with customers — no login required.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', bgcolor: 'white', px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
              >
                {publicFormUrl}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={handleCopyLink}
                color={copied ? 'success' : 'primary'}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(publicFormUrl, '_blank')}
              >
                Preview
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              placeholder="Search name, email, postcode…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
              sx={{ minWidth: 260 }}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as QuoteRequestStatus | '')}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {Object.values(QuoteRequestStatus).map((s) => (
                <MenuItem key={s} value={s}>
                  {QUOTE_REQUEST_STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Postcode</TableCell>
                <TableCell>Interest</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Received</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Loading…
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No quote requests yet
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((qr) => (
                  <TableRow
                    key={qr.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelected(qr)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {qr.full_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{qr.email}</Typography>
                      {qr.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {qr.phone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {qr.postcode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {PRODUCT_INTEREST_LABELS[qr.product_interest]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={QUOTE_REQUEST_STATUS_LABELS[qr.status]}
                        color={QUOTE_REQUEST_STATUS_COLORS[qr.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(qr.created_at).toLocaleDateString('en-GB')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Create quote for this request">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            navigate('/quotes/calculator', {
                              state: { quoteRequest: qr },
                            })
                          }
                        >
                          <ArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteMutation.mutate(qr.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Detail / status dialog */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
      >
        {selected && (
          <>
            <DialogTitle>
              Quote Request — {selected.full_name}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography>{selected.email}</Typography>
                      <IconButton size="small" onClick={() => copyToClipboard(selected.email)}>
                        <CopyIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  </Box>
                  {selected.phone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography>{selected.phone}</Typography>
                    </Box>
                  )}
                </Stack>

                <Box>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography>{selected.address_line_1}</Typography>
                  <Typography>
                    {selected.city} · <strong>{selected.postcode}</strong>
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">Interest</Typography>
                  <Typography>{PRODUCT_INTEREST_LABELS[selected.product_interest]}</Typography>
                </Box>

                {selected.message && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Message</Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selected.message}</Typography>
                  </Box>
                )}

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Update status
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {Object.values(QuoteRequestStatus).map((s) => (
                      <Button
                        key={s}
                        size="small"
                        variant={selected.status === s ? 'contained' : 'outlined'}
                        onClick={() => updateMutation.mutate({ id: selected.id, status: s })}
                        disabled={updateMutation.isLoading}
                      >
                        {QUOTE_REQUEST_STATUS_LABELS[s]}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Close</Button>
              <Button
                variant="contained"
                startIcon={<ArrowIcon />}
                onClick={() => {
                  navigate('/quotes/calculator', { state: { quoteRequest: selected } });
                  setSelected(null);
                }}
              >
                Build Quote
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default QuoteRequestsPage;
